/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DOCUMENT } from "@angular/common";
import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Inject, Injectable, Injector } from "@angular/core";

import { SnackbarComponent } from "./snackbar.component";

/**
 * Options for displaying the snackbar
 */
export type SnackbarOptions = {
  type?: string;
  /** Duration expressed in ms until snackbar will be hidden. If a negative number it will stay on screen (until the user manually removes it). Default is 5000ms. */
  duration?: number;
  /** The snackbar title. */
  title?: string;
  /** The message text to display in the button. If empty the button will stay hidden. */
  buttonMessage?: string;
  /** The element where the snackbar will be displayed. Default is `document.body`. */
  parentElement?: HTMLElement;
  /** Class to add on the wrapper element. */
  wrapperElementClass?: string;
};

/**
 * Default duration expressed in ms until snackbar will be hidden
 */
const DURATION_UNTIL_HIDDEN = 5_000;

/**
 * This service allows the import of snackbar into components.
 *
 * Snackbar can be used to alert use to messages after a form submit.
 *
 * The reference to the service will be placed in the element ts file, and span slots like usual will be added to html file. The duration for how long the snack will be visible on screen can be customized as well as the parent element for the snackbar.
 *
 * **Usage**
 *
 * ```typescript
 * import { SnackbarService } from "@vattenfall/ui";
 *
 * @Component({ ... })
 * export class ExampleComponent {
 *   constructor(private elementRef: ElementRef<HTMLElement>, private snackbarService: SnackbarService) {
 *     // Displays a success message
 *     this.snackbarService.message("We made it!")
 *     // Displays a message with a button
 *     this.snackbarService.message("We made it!", { buttonMessage: "Close" })
 *     // Displays a message on the bottom of the component
 *     this.snackbarService.message("We made it!", { parentElement: elementRef.nativeElement })
 *   }
 *
 * }
 */
@Injectable({
  providedIn: "root",
})
export class SnackbarService {
  parentElements = new Map<HTMLElement, HTMLElement>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private appRef: ApplicationRef,
    private resolver: ComponentFactoryResolver,
    private injector: Injector
  ) {}

  /**
   * Displays a snackbar message at the bottom of the screen (or parent element if provided) that is hidden after a specified duration of time.
   */
  message(message = "", options: SnackbarOptions = {}) {
    const parentElement = options.parentElement ?? this.document.body;
    // Add wrapper to parent element if it doesn't already exist
    if (!this.parentElements.has(parentElement)) {
      this.parentElements.set(parentElement, this.getWrapperElement(parentElement));
      parentElement.appendChild(this.parentElements.get(parentElement)!);
    }

    const duration = options.duration ?? DURATION_UNTIL_HIDDEN;
    const wrapperElement = this.parentElements.get(parentElement)!;

    if (options.wrapperElementClass) wrapperElement.classList.add(options.wrapperElementClass);

    const componentRef = this.resolver.resolveComponentFactory(SnackbarComponent).create(this.injector);

    componentRef.instance.message = message;

    if (options.buttonMessage) componentRef.instance.buttonMessage = options.buttonMessage;
    if (options.title) componentRef.instance.title = options.title;
    if (options.type) componentRef.instance.type = options.type;

    this.appRef.attachView(componentRef.hostView);

    const messageElement = (componentRef.hostView as EmbeddedViewRef<SnackbarComponent>).rootNodes[0] as HTMLElement;

    wrapperElement.appendChild(messageElement);

    const snackbarElement = componentRef.location.nativeElement.firstElementChild;

    setTimeout(() => {
      snackbarElement && snackbarElement.classList.add("slide-out");
    }, duration);
    // setTimeout below 500ms extra time to see the effect slideOut before it's removed from the DOM
    setTimeout(() => {
      componentRef.instance.isDisplay = false;
      wrapperElement.removeChild(messageElement);
    }, duration + 500);
  }

  private getWrapperElement(parentElement: HTMLElement) {
    const isParentElementBody = this.document.body.isSameNode(parentElement);
    if (isParentElementBody) return this.getFixedPositionElement();
    return this.getAbsolutePositionElement();
  }

  private getFixedPositionElement() {
    const wrapperElement = this.document.createElement("div");
    wrapperElement.style.bottom = "0.625rem";
    wrapperElement.style.left = "50%";
    wrapperElement.style.position = "fixed";
    wrapperElement.style.transform = "translateX(-50%)";
    wrapperElement.style.width = "100%";
    wrapperElement.style.zIndex = "999";
    return wrapperElement;
  }

  private getAbsolutePositionElement() {
    const wrapperElement = this.document.createElement("div");
    wrapperElement.style.bottom = "0.625rem";
    wrapperElement.style.left = "50%";
    wrapperElement.style.position = "absolute";
    wrapperElement.style.transform = "translateX(-50%)";
    wrapperElement.style.width = "100%";
    wrapperElement.style.zIndex = "999";
    return wrapperElement;
  }
}
